import Link from "next/link";
import { Stack } from "@mui/material";

import { SSText } from "src/components/base/components/SSText";

import { routes } from "src/shared/routes";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";
import type { TRegistrationCardData } from "src/shared/types/globalStotre.types";

export const REGISTRATION_CARDS: TRegistrationCardData[] = [
  {
    title: "Purchase New York Yankees Upgrades",
    description:
      "Enhance your gameday experience by upgrading your New York Yankees tickets here. To purchase your upgrade, please log in with the same email address you use to manage your tickets. Upgrade purchases are non-refundable. Upgrades purchased in connection with this program are for personal use only and may not be transferred or resold. You must have a valid game ticket to today's game in order to participate in this exclusive opportunity. Offer valid for select, non-premium seating areas. Offer is online only, for a limited time only, subject to availability, has no cash value, is not valid on previously purchased tickets, and cannot be used in conjunction with any other promotional offer. In the event a regulation game (as defined by MLB) is not played, upgrades purchased in connection with this program will be cancelled and refunded.",
    buttonLabel: "View Available Upgrades",
    authRedirectPage: routes.home,
  },
];

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "Why don't I see seat upgrades available for purchase?",
    answer:
      "You must have a valid game ticket to today's game in order to participate in this exclusive opportunity. Offer valid for select, non-premium seating areas.",
  },
  {
    question: "Will there be seat upgrades available for every game?",
    answer:
      "Upgrades are subject to availability. Please check prior to your game to view available upgrades.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "After successful purchase, your tickets will be available in the MLB Ballpark app.",
  },
  {
    question: "Where will my seats be located?",
    answer:
      "Seats will be located in the sections selected in your upgrade purchase. After successful purchase, Section/Row/Seat will be displayed on your new tickets available in the MLB Ballpark app.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Upgrades purchased in connection with this program are for personal use only and may not be transferred or resold.",
  },
  {
    question: "Can my seats be changed?",
    answer:
      "After successful purchase, your tickets cannot be changed. Upgrade purchases are non-refundable.",
  },
];

export const TERMS_AND_CONDITION = () => (
  <>
    Data you submit in connection with your purchase will be provided to the New
    York Yankees for the New York Yankees&apos; use for fulfillment purposes and
    otherwise in accordance with the New York Yankees{" "}
    <Link
      target="_blank"
      href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
    >
      Privacy Policy
    </Link>
    . In connection with your purchase, you will receive ticket-related
    commercial emails from yankees.com. You may unsubscribe at any time. By
    checking this box you agree to receive additional commercial emails from
    yankees.com and MLB. If you purchase tickets, you may receive customer service messages
    via email from the New York Yankees, including optional surveys regarding
    your baseball experience. By clicking the Purchase button, you agree to
    accept all Terms and Conditions and you acknowledge and agree that your credit card will be charged. By
    clicking the Purchase button, you agree to the{" "}
    <Link
      href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
      target="_blank"
    >
      MLB Privacy Policy
    </Link>
    ,{" "}
    <Link
      href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
      target="_blank"
    >
      Terms of Use
    </Link>{" "}
    and to receive information regarding your ticket(s) via an SMS text message
    to the mobile of use. Message and data rates may apply. Tickets will be made
    available in the MLB Ballpark app only. MLB and supported mobile device
    required. All sales are final. There are no refunds or exchanges.
  </>
);

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 1,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self"
        );
      },
    },
    {
      id: 2,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self"
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};

export const FooterLinks = () => {
  const theme = useSSTheme();
  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};
